/**
 * Notification-related type definitions
 */

/**
 * Global notification preferences stored in notification_preferences table
 */
export interface NotificationPreferences {
  user_id: string;
  notif_event_reminders: boolean;
  notif_event_changes: boolean;
  notif_community_comments: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Insert type for notification preferences
 */
export interface NotificationPreferencesInsert {
  user_id: string;
  notif_event_reminders?: boolean;
  notif_event_changes?: boolean;
  notif_community_comments?: boolean;
}

/**
 * Update type for notification preferences
 */
export interface NotificationPreferencesUpdate {
  notif_event_reminders?: boolean;
  notif_event_changes?: boolean;
  notif_community_comments?: boolean;
}

/**
 * Per-event notification subscriptions stored in event_notification_subscriptions table
 */
export interface EventNotificationSubscription {
  id: number;
  user_id: string;
  event_id: number;
  remind_3_days: boolean;
  remind_1_day: boolean;
  remind_1_hr: boolean;
  notify_changes: boolean;
  created_at: string;
}

/**
 * Insert type for event notification subscriptions
 */
export interface EventNotificationSubscriptionInsert {
  user_id: string;
  event_id: number;
  remind_3_days?: boolean;
  remind_1_day?: boolean;
  remind_1_hr?: boolean;
  notify_changes?: boolean;
}

/**
 * Update type for event notification subscriptions
 */
export interface EventNotificationSubscriptionUpdate {
  remind_3_days?: boolean;
  remind_1_day?: boolean;
  remind_1_hr?: boolean;
  notify_changes?: boolean;
}

/**
 * Notification types for tracking sent notifications
 */
export type NotificationType =
  | 'event_created'
  | 'remind_3_days'
  | 'remind_1_day'
  | 'remind_1_hr'
  | 'event_change'
  | 'event_cancelled'
  | 'post_comment'
  | 'post_approved'
  | 'post_rejected'
  | 'post_pending';

export type SentNotificationType =
  | 'remind_3_days'
  | 'remind_1_day'
  | 'remind_1_hr'
  | 'event_change'
  | 'event_cancelled';

export type NotificationDestinationKind = 'event' | 'post' | 'admin_queue';
export type NotificationPushStatus = 'pending' | 'skipped' | 'sent' | 'failed';

/**
 * Sent notification record
 */
export interface SentNotification {
  id: number;
  user_id: string;
  event_id: number;
  notification_type: SentNotificationType;
  sent_at: string;
}

/**
 * Durable in-app notification record stored in user_notifications
 */
export interface UserNotification {
  id: string;
  user_id: string;
  notification_type: NotificationType;
  destination_kind: NotificationDestinationKind;
  destination_url: string;
  delivery_key: string;
  title: string;
  body: string;
  event_id: number | null;
  post_id: number | null;
  metadata: Record<string, unknown>;
  onesignal_notification_id: string | null;
  push_status: NotificationPushStatus;
  push_attempted_at: string | null;
  push_attempt_count: number;
  push_last_error: string | null;
  created_at: string;
  read_at: string | null;
}

/**
 * Individual reminder time options for multi-select
 * Values match the column names in event_notification_subscriptions table
 */
export enum ReminderTimeOption {
  THREE_DAYS = 'remind_3_days',
  ONE_DAY = 'remind_1_day',
  ONE_HOUR = 'remind_1_hr',
}

// ============================================================================
// UI Component Types
// ============================================================================

/**
 * State for the test notification button in NotificationSettings
 */
export type TestNotificationState =
  | 'idle'
  | 'countdown'
  | 'sending'
  | 'sent'
  | 'error';

/**
 * Simplified notification preferences for UI components
 * (subset of NotificationPreferences without DB metadata)
 */
export interface NotificationPreferencesUI {
  notif_event_reminders: boolean;
  notif_event_changes: boolean;
  notif_community_comments: boolean;
}

/**
 * Props for the NotificationSettings presentational component
 */
export interface NotificationSettingsProps {
  /** Whether push notifications are enabled for this device (undefined while checking) */
  pushEnabled: boolean | undefined;
  /** Whether we're still checking the permission status */
  checkingPermission: boolean;
  /** User's notification preferences */
  preferences: NotificationPreferencesUI;
  /** Whether preferences are being updated */
  isUpdating: boolean;
  /** Current state of the test notification button */
  testState: TestNotificationState;
  /** Countdown seconds for test notification */
  countdown: number;
  /** Handler to enable push notifications */
  onEnablePush: () => void;
  /** Handler to toggle a preference */
  onTogglePreference: (
    key:
      | 'notif_event_reminders'
      | 'notif_event_changes'
      | 'notif_community_comments'
  ) => void;
  /** Handler to send a test notification */
  onSendTestNotification: () => void;
}
